import axios from 'axios';

/**
 * Super Admin API client — separate JWT from tenant users.
 * Token stored in localStorage under `super_admin_token` to avoid clashes.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const sa = axios.create({
  baseURL: `${BACKEND_URL}/api/super-admin`,
  headers: { 'Content-Type': 'application/json' },
});

sa.interceptors.request.use((config) => {
  const token = localStorage.getItem('super_admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

sa.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('super_admin_token');
      const path = window.location.pathname;
      // Avoid redirect loop AND avoid re-throwing when the login/layout already handles it
      if (!path.startsWith('/super-admin/login')) {
        window.location.href = '/super-admin/login';
        // Return an empty promise to avoid the uncaught error overlay during the redirect
        return new Promise(() => {});
      }
    }
    return Promise.reject(err);
  }
);

export const superAdminApi = {
  login: (email, password) => sa.post('/login', { email, password }),
  me: () => sa.get('/me'),
  listTenants: () => sa.get('/tenants'),
  createTenant: (body) => sa.post('/tenants', body),
  getTenant: (id) => sa.get(`/tenants/${id}`),
  updateTenant: (id, body) => sa.patch(`/tenants/${id}`, body),
  suspendTenant: (id) => sa.post(`/tenants/${id}/suspend`),
  activateTenant: (id) => sa.post(`/tenants/${id}/activate`),
  deleteTenant: (id, confirmSlug) => sa.delete(`/tenants/${id}`, { params: { confirm_slug: confirmSlug } }),
  impersonate: (id, body) => sa.post(`/tenants/${id}/impersonate`, body),
  dashboard: () => sa.get('/dashboard'),
  insights: () => sa.get('/insights'),
  timeline: (params = {}) => sa.get('/insights/timeline', { params }),
  captureSnapshots: () => sa.post('/snapshots/capture-now'),
  auditLog: (params = {}) => sa.get('/audit-log', { params }),
  getBranding: (id) => sa.get(`/tenants/${id}/branding`),
  updateBranding: (id, body) => sa.patch(`/tenants/${id}/branding`, body),
  previewHealthReport: (id) => `${BACKEND_URL}/api/super-admin/tenants/${id}/health-report/preview`,
  sendHealthReportsNow: () => sa.post('/health-reports/send-now'),
  listCustomTemplates: (id) => sa.get(`/tenants/${id}/custom-templates`),
  createCustomTemplate: (id, body) => sa.post(`/tenants/${id}/custom-templates`, body),
  updateCustomTemplate: (id, tplId, body) => sa.patch(`/tenants/${id}/custom-templates/${tplId}`, body),
  deleteCustomTemplate: (id, tplId) => sa.delete(`/tenants/${id}/custom-templates/${tplId}`),
  applyCustomTemplates: (id, body) => sa.post(`/tenants/${id}/custom-templates/apply`, body),
  checkAlertsNow: () => sa.post('/alerts/check-now'),
};

// Onboarding API (uses main user JWT — imported from lib/api indirectly)
import mainApi from './api';
export const onboardingApi = {
  status: () => mainApi.get('/onboarding/status'),
  complete: (body) => mainApi.post('/onboarding/complete', body),
  skip: () => mainApi.post('/onboarding/skip'),
};

export default sa;
